import { Alert, Form, Space, Typography, Button } from 'antd'
import AuthorisedContainer from 'components/AuthorisedContainer'
import { PermissionSetMap } from 'constants/RBACConstants'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ToolStatusMappingAPI } from 'restAPI/ToolsIntegrationAPI'
import { FieldTypes, generateFormFields } from 'utils/formUtils'
import { capitalize, map, replace, isEmpty, omit, flatMap, toLower } from 'utils/lodashUtils'
import { useGlobalFilterContext } from 'contexts/GlobalFilterContext';
import { TOOL_STATUS, DEFAULT_STATUS_MAPPING_NEW, STATUS_OPTIONS, MODIFY_STATUS_NAME_TOOLS, TOOL_STATUS_VOCABULARY_MAP } from './SettingStatusConstant'
import Vocabulary from 'components/Vocabulary';
import { toolNameForAPI } from 'views/Integrations/GenericPullAndPushToolView';
import useEnabledFindingStatus from 'hooks/useEnabledFindingStatus';
import { useFeatureFlag } from "contexts/FeatureFlagContext";

const ToolSettingsStatusMapping = ({ toolName, handleToolStatusChange }) => {
    const apiToolName = toolNameForAPI(toolName, true)
    const [form] = Form.useForm();
    const { globalFilterHash } = useGlobalFilterContext();
    const [values, setValues] = useState({});
    const enabledFindingStatus = map(useEnabledFindingStatus(), status => toLower(status).replace(/_/g, ''));
    const featureFlags = useFeatureFlag();

    const defaultMappedData = useMemo(() => {
        const mapping = DEFAULT_STATUS_MAPPING_NEW[apiToolName] || {};
        return Object.fromEntries(
          Object.entries(mapping).map(([ category, opts ]) => [
            category,
            opts
              .filter(item =>
                typeof item === 'string'
                  ? true
                  : (item.featureFlag === undefined || featureFlags[item.featureFlag])
              )
              .map(item =>
                typeof item === 'string' ? item : item.status
              )
          ])
        );
      }, [apiToolName, featureFlags]);

    const fetchData = useCallback(async () => {
        try {
            const resData = await ToolStatusMappingAPI.getToolStatusMapping(apiToolName).promise;
            const initValues = isEmpty(resData) ? defaultMappedData : resData.config.status;
            form.setFieldsValue(initValues);
            setValues(initValues)
            handleToolStatusChange({id: resData.id, statusMapping: initValues})
        } catch (err) {
            console.error(err)
        }
    }, [apiToolName, form, handleToolStatusChange, defaultMappedData]);

    useEffect(() => {
        fetchData();
    }, [globalFilterHash, fetchData])

    const getFilteredStatuses = useCallback(name => {
        return (STATUS_OPTIONS[name] || [])
          .filter(item =>
            typeof item === 'string'
              ? true
              : (item.featureFlag === undefined || featureFlags[item.featureFlag])
          )
          .map(item =>
            typeof item === 'string' ? item : item.status
          );
      }, [featureFlags]);

    const getFormOptions = useCallback((fieldKey, values) => {
        // fetch selected data from the form while edit in customFieldMapping
        let formData = omit(values, toLower(fieldKey));
        let selectedKeys = flatMap(formData).filter(key => key !== undefined);
        const allowedStatuses = getFilteredStatuses(apiToolName);
        return map(allowedStatuses, option => ({
            label: MODIFY_STATUS_NAME_TOOLS.includes(apiToolName) ? capitalize(replace(option, /_/g, ' ')) : option,
            value: option,
            disabled: selectedKeys.indexOf(option) !== -1
        }))
    }, [apiToolName, getFilteredStatuses]);

    const commonStatusProps = useMemo(() => ({
        colSpan: 8,
        type: FieldTypes.MULTI_SELECT,
        props: {
            allowClear: true
        }
    }), [])

    const fields = useMemo(() => [{
        name: "statusMapping",
        type: FieldTypes.GROUP,
        fields: TOOL_STATUS.filter(status => enabledFindingStatus.includes(status)).map(item => ({
            name: item,
            label: <Vocabulary name={TOOL_STATUS_VOCABULARY_MAP[item]} />,
            options: getFormOptions(item, values),
            ...commonStatusProps
        }))
    }], [getFormOptions, commonStatusProps, values, enabledFindingStatus])  

    // **************  Reset the Default value from database *********
    const onReset = useCallback(() => {
        form.resetFields();
        form.setFieldsValue(defaultMappedData);
        handleToolStatusChange({statusMapping: defaultMappedData});
        setValues(defaultMappedData)
      },[form, handleToolStatusChange, defaultMappedData]);


    return (
        <Space direction="vertical" className="m-b-m">
            <Typography.Text>{toolName} tool to ArmorCode Status Mapping</Typography.Text>
            <Alert
                description={
                    <Form
                        form={form}
                        style={{ minWidth: 600 }}
                        layout="vertical"
                        onValuesChange={(_, values) => {
                            setValues(values);
                            handleToolStatusChange({statusMapping: values})
                        }}
                    >
                        {generateFormFields(fields)}

                        <Form.Item className="text-right p-x-sm" >
                            <AuthorisedContainer permission={PermissionSetMap.UPDATE_SLA}>
                                <Button type="primary" onClick={onReset}>Reset</Button>
                            </AuthorisedContainer>
                         </Form.Item>    
                    </Form>
                }
            />
        </Space>
    )
}

export default ToolSettingsStatusMapping
